import React, { useState } from 'react';
import { Plus, Building2, Compass, Cpu, BookOpen, Ruler, Boxes, Combine, Waves, Sparkles, Ticket, Heart, Landmark, Projector, GalleryHorizontalEnd, Store, Drama } from 'lucide-react';
import Reveal from './Reveal';
import { SectionMarker } from './Gap';
import { disciplineData, becomesReal } from '../data2';
import { motion } from 'framer-motion';
import { Kern } from './Kern';

const disciplineIcons = ['/icons/disc-story.png?v=2', '/icons/disc-architecture.png?v=2', '/icons/disc-experience.png?v=2', '/icons/disc-technology.png?v=2', '/icons/disc-production.png?v=2'];
const movementIcons = ['/icons/convergence.png', '/icons/orchestration.png', '/icons/transformation.png?v=2'];
const clientIcons = [Ticket, Heart, Landmark, Building2, Projector, Drama, GalleryHorizontalEnd, Store];

const BentoTile = ({ c, grow, expanded, onEnter, onSelect }) => (
  <motion.div
    onMouseEnter={onEnter}
    onClick={onSelect}
    initial={false}
    animate={{ flexGrow: grow }}
    transition={{ duration: 0.6, ease: [0.22, 1, 0.36, 1] }}
    style={{ flexBasis: 0, flexShrink: 1, backgroundColor: '#0A0D17' }}
    data-testid="bento-tile"
    className={`group relative min-w-0 min-h-0 h-full rounded-xl overflow-hidden border transition-colors duration-500 cursor-pointer ${expanded ? 'border-[#838BDA]' : 'border-[rgba(255,255,255,0.12)] hover:border-[#838BDA]'}`}
  >
    <img src={c.img} alt="" aria-hidden="true" className="absolute inset-0 w-full h-full object-cover" loading="lazy" />
    <div className="absolute inset-0 pointer-events-none" style={{ background: 'linear-gradient(180deg, rgba(10,13,23,0) 0%, rgba(10,13,23,0) 48%, rgba(10,13,23,0.35) 70%, rgba(10,13,23,0.78) 88%, rgba(10,13,23,0.92) 100%)' }} />
    <div className="relative flex flex-col h-full p-5 md:p-6 pointer-events-none">
      <div className="mt-auto">
        <h3 className="font-display text-[1.15rem] md:text-[1.4rem] font-medium leading-[1.15] text-[var(--accent)] break-words">{c.name}</h3>
        <div className={`grid transition-all duration-500 ease-[cubic-bezier(0.16,1,0.3,1)] ${expanded ? 'grid-rows-[1fr] opacity-100 translate-y-0 mt-3' : 'grid-rows-[0fr] opacity-0 translate-y-3'}`}>
          <div className="overflow-hidden">
            <p className="text-[var(--muted)] text-[0.85rem] md:text-[0.95rem] leading-relaxed font-light w-full">{c.desc}</p>
          </div>
        </div>
      </div>
    </div>
  </motion.div>
);

// Desktop: 3 rows of 3. Hovering (or tapping) a card expands it and compresses only the
// other cards in the SAME row (row-wise). Titles wrap/stack so they never get cut off.
const DesktopBento = ({ clients }) => {
  const [hovered, setHovered] = useState(null); // client.no on hover
  const [pinned, setPinned] = useState(null);   // client.no on click/tap
  const active = hovered !== null ? hovered : pinned;
  const rows = [clients.slice(0, 3), clients.slice(3, 6), clients.slice(6, 9)];
  return (
    <div
      onMouseLeave={() => setHovered(null)}
      className="hidden md:flex md:flex-col gap-4 h-[715px]"
      data-testid="bento-desktop"
    >
      {rows.map((row, r) => {
        const rowActive = row.some((c) => c.no === active);
        return (
          <div key={r} className="flex gap-4 flex-1 min-h-0">
            {row.map((c) => {
              const grow = !rowActive ? 1 : (c.no === active ? 2.6 : 0.7);
              return (
                <BentoTile
                  key={c.no}
                  c={c}
                  grow={grow}
                  expanded={c.no === active}
                  onEnter={() => setHovered(c.no)}
                  onSelect={() => setPinned((prev) => (prev === c.no ? null : c.no))}
                />
              );
            })}
          </div>
        );
      })}
    </div>
  );
};

const MobileTile = ({ c, orphan, i }) => {
  const [open, setOpen] = useState(false);
  return (
    <button
      type="button"
      onClick={() => setOpen((o) => !o)}
      aria-expanded={open}
      data-testid={`mobile-client-tile-${i}`}
      className={`group relative text-left rounded-xl overflow-hidden border border-[rgba(255,255,255,0.12)] active:border-[#838BDA] transition-colors h-[215px] ${orphan ? 'col-span-2' : ''}`}
    >
      <img src={c.img} alt="" aria-hidden="true" className="absolute inset-0 w-full h-full object-cover" loading="lazy" />
      <div className="absolute inset-0 pointer-events-none" style={{ background: 'linear-gradient(180deg, rgba(10,13,23,0) 35%, rgba(10,13,23,0.65) 70%, rgba(10,13,23,0.94) 100%)' }} />

      {/* tap cue */}
      <span className={`absolute top-2.5 right-2.5 w-6 h-6 rounded-full border flex items-center justify-center shadow-md transition-all duration-300 z-10 ${open ? 'rotate-45 bg-[var(--accent)] border-[var(--accent)]' : 'bg-transparent border-white/70 backdrop-blur-sm'}`}>
        <Plus size={13} className={open ? 'text-[#0A0D17]' : 'text-white'} />
      </span>

      {/* readability scrim, fades in on open */}
      <div className={`absolute inset-0 pointer-events-none transition-opacity duration-500 ${open ? 'opacity-100' : 'opacity-0'}`} style={{ background: 'linear-gradient(180deg, rgba(10,13,23,0.1) 0%, rgba(10,13,23,0.55) 42%, rgba(10,13,23,0.9) 100%)', backdropFilter: 'blur(2px)', WebkitBackdropFilter: 'blur(2px)' }} />

      {/* bottom-anchored content: the single title is pushed up as the body expands beneath it */}
      <div className="absolute inset-x-0 bottom-0 p-3">
        <h3 className="font-display text-[0.95rem] font-medium leading-tight text-[var(--accent)] break-words">{c.name}</h3>
        <div className={`grid transition-all duration-500 ease-[cubic-bezier(0.16,1,0.3,1)] ${open ? 'grid-rows-[1fr] opacity-100 mt-2' : 'grid-rows-[0fr] opacity-0'}`}>
          <div className="overflow-hidden">
            <p className="text-[var(--muted)] text-[clamp(0.68rem,2.7vw,0.8rem)] leading-[1.4] font-light">{c.desc}</p>
          </div>
        </div>
      </div>
    </button>
  );
};

const MobileBento = ({ clients }) => (
  <div className="grid grid-cols-2 gap-3 md:hidden">
    {clients.map((c, i) => (
      <MobileTile key={c.no} c={c} i={i} orphan={i === clients.length - 1 && clients.length % 2 === 1} />
    ))}
  </div>
);

const ClientBento = ({ clients }) => (
  <>
    <MobileBento clients={clients} />
    <DesktopBento clients={clients} />
  </>
);

const DisciplineRow = ({ d, Icon }) => {
  const [open, setOpen] = useState(false);
  return (
  <div
    tabIndex={0}
    onClick={() => setOpen((o) => !o)}
    onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); setOpen((o) => !o); } }}
    className="group relative border-t border-[var(--border)] outline-none cursor-pointer transition-colors duration-300 hover:bg-[var(--surface)]/50 focus-within:bg-[var(--surface)]/50"
  >
    {/* left accent bar */}
    <span className={`absolute left-0 top-0 h-full w-[3px] bg-[var(--accent-blue)] origin-top transition-transform duration-500 md:scale-y-0 md:group-hover:scale-y-100 md:group-focus-within:scale-y-100 ${open ? 'scale-y-100' : 'scale-y-0'}`} />
    <div className="w-full flex items-center gap-4 md:gap-5 py-7 pl-4 md:pl-6">
      {typeof Icon === 'string'
        ? <img src={Icon} alt="" aria-hidden="true" className="shrink-0 w-9 h-9 object-contain group-hover:scale-110 transition-transform duration-300" />
        : <Icon size={36} strokeWidth={1.4} className="shrink-0 text-[var(--accent)] group-hover:scale-110 transition-transform duration-300" />}
      <span className="font-display text-[1.5rem] md:text-[2rem] font-medium flex-1 text-[var(--text)] group-hover:text-[var(--accent)] md:group-hover:translate-x-1.5 transition-all duration-300">{d.title}</span>
      <span className="label hidden lg:block  text-[var(--muted-2)] mr-6 group-hover:text-[var(--accent-blue)] transition-colors duration-300">{d.sub}</span>
      <span className={`w-9 h-9 rounded-full border flex items-center justify-center shrink-0 transition-all duration-500 md:border-[var(--border-strong)] md:group-hover:border-[var(--accent)] md:group-hover:bg-[var(--accent)] md:group-hover:rotate-[135deg] ${open ? 'border-[var(--accent)] bg-[var(--accent)] rotate-[135deg]' : 'border-[var(--border-strong)]'}`}>
        <Plus size={16} className={`transition-colors duration-300 md:text-[var(--muted)] md:group-hover:text-[#0A1030] ${open ? 'text-[#0A1030]' : 'text-[var(--muted)]'}`} />
      </span>
    </div>
    {/* tap-to-open on touch, hover-expand on desktop */}
    <div className={`grid transition-[grid-template-rows] duration-500 ease-[cubic-bezier(0.16,1,0.3,1)] md:grid-rows-[0fr] md:group-hover:grid-rows-[1fr] md:group-focus-within:grid-rows-[1fr] ${open ? 'grid-rows-[1fr]' : 'grid-rows-[0fr]'}`}>
      <div className="overflow-hidden">
        <div className={`space-y-3 max-w-[70ch] pb-8 pl-4 md:pl-[5rem] transition-all duration-500 delay-100 md:opacity-0 md:translate-y-2 md:group-hover:opacity-100 md:group-hover:translate-y-0 md:group-focus-within:opacity-100 md:group-focus-within:translate-y-0 ${open ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-2'}`}>
          {(Array.isArray(d.text) ? d.text : [d.text]).map((para, i) => (
            <p key={i} className={`leading-relaxed ${i === 0 ? 'text-[var(--text)] font-medium' : 'text-[var(--muted)] font-light'}`}>{para}</p>
          ))}
        </div>
      </div>
    </div>
  </div>
  );
};

const Synthesis = () => {
  const d = disciplineData;
  const r = becomesReal;

  return (
    <section id="synthesis" className="relative section-pad border-t border-[var(--border)] bg-[var(--bg-2)]">
      <div className="max-w-[1500px] mx-auto px-6 md:px-10">
        <Reveal>
          <h2 className="font-display font-medium text-[clamp(1.9rem,4vw,3.4rem)] leading-[1.06] tracking-[-0.02em] mb-[var(--section-pad)]">
            {d.headline[0]}<br />
            <span className="text-[var(--muted)]">{d.headline[1]}</span>{' '}
            <span className="font-serif-c italic text-[var(--accent)] font-normal">{d.headline[2]}</span>
          </h2>
        </Reveal>

        {/* We work as one mind - two columns (40 / 60) */}
        <div className="grid md:grid-cols-5 gap-10 md:gap-16">
          <div className="md:col-span-2">
            <Reveal><p className="font-serif-c italic text-[1.15rem] md:text-[1.45rem] leading-snug text-[var(--accent)] text-left">{d.oneMind.call}</p></Reveal>
          </div>
          <div className="md:col-span-3">
            <Reveal delay={120}><p className="text-[var(--muted)] leading-relaxed font-light text-left md:text-right w-full">{d.oneMind.paras.join(' ')}</p></Reveal>
          </div>
        </div>

        {/* The discipline / three movements - stacked */}
        <div id="discipline" className="mt-[var(--section-pad)] mb-[var(--section-pad)] scroll-mt-24">
          <Reveal><p className="label !text-[var(--accent)] label-gap">{r.disciplineLabel}</p></Reveal>
          <Reveal><h2 className="font-display font-medium text-[clamp(1.9rem,4vw,3.4rem)] leading-[1.06] tracking-[-0.02em] w-full">Three movements, one connected process.</h2></Reveal>
        </div>

        <div className="grid md:grid-cols-3 gap-8 mb-[var(--section-pad)]">
          {r.movements.map((m, i) => {
            const iconSrc = movementIcons[i];
            return (
            <Reveal key={m.no} delay={i * 120}>
              <div className="group h-full flex flex-col p-6 md:p-8 rounded-xl border border-[var(--border)] bg-[var(--surface)]/40 hover:border-[var(--accent-blue)] hover:-translate-y-1 transition-all duration-300">
                <div className="flex items-center justify-between mb-6 md:mb-8">
                  <span className="flex items-center justify-center">
                    <img src={iconSrc} alt="" aria-hidden="true" className="w-10 h-10 md:w-12 md:h-12 object-contain" />
                  </span>
                  <span className="font-display text-[2.2rem] md:text-[3rem] font-medium leading-none" style={{ color: 'var(--accent-blue)' }}>{m.no}</span>
                </div>
                <span className="label !text-[var(--accent)] block mb-3" style={{ fontFamily: "'Empax', sans-serif", letterSpacing: '0.14em' }}>{m.label}</span>
                <h3 className="text-[1.25rem] md:text-[1.5rem] leading-[1.2] font-medium mb-4 md:min-h-[3.6rem]" style={{ fontFamily: "'Inter', sans-serif" }}>{m.title}</h3>
                <p className="text-[var(--muted)] leading-relaxed font-light mb-8">{m.text}</p>
                <p className="label !text-[var(--accent)] font-bold w-full text-left md:text-center whitespace-normal md:whitespace-nowrap mt-auto pt-5 border-t border-[var(--border)]" style={{ fontFamily: "'Empax', sans-serif", letterSpacing: '0.12em', fontSize: 'var(--card-tag-size)', lineHeight: 'var(--card-tag-leading)' }}>{m.tags}</p>
              </div>
            </Reveal>
            );
          })}
        </div>

        {/* Connecting line */}
        <Reveal>
          <p className="font-serif-c italic text-[var(--text)] text-[1.6rem] md:text-[2.2rem] text-left md:text-center pt-5 pb-5 mb-[var(--section-pad)]">
            <span className="block">Every experience is shaped through</span>
            <span className="text-[var(--accent)] block mt-1 md:mt-0">
              <span className="block md:inline">Customization<span className="hidden md:inline">, </span></span>
              <span className="block md:inline">Personalization<span className="hidden md:inline"> and </span></span>
              <span className="block md:inline">Premiumization<span className="hidden md:inline">,</span></span>
            </span>
            <span className="block mt-1 md:mt-0">making it distinct to its story, meaningful to its audience, and considered in every detail.</span>
          </p>
        </Reveal>

        {/* Six disciplines */}
        <div id="crafts" className="mb-14 scroll-mt-24">
          <Reveal>
            <p className="label !text-[var(--accent)] label-gap">{r.sixLabel}</p>
            <h2 className="font-display font-medium text-[clamp(1.9rem,3.6vw,3rem)] leading-[1.08] tracking-[-0.01em]">
              {r.sixHeading[0]} <span className="text-[var(--accent)]">{r.sixHeading[1]}</span>
            </h2>
          </Reveal>
        </div>
        <div className="mb-16 md:mb-44 border-b border-[var(--border)]">
          {r.disciplines.map((disc, i) => (
            <DisciplineRow key={disc.fig} d={disc} Icon={disciplineIcons[i]} />
          ))}
        </div>

        {/* The Empax Collective, small paragraph */}
        <div className="hidden mb-32 md:mb-44 pt-14 border-t border-[var(--border)] grid md:grid-cols-12 gap-8 md:gap-16">
          <div className="md:col-span-4">
            <Reveal>
              <p className="label label-gap">The Empax Collective</p>
              <h3 className="font-serif-c italic text-[clamp(1.8rem,3vw,2.6rem)] text-[var(--accent)]">The team grows when the idea needs it.</h3>
            </Reveal>
          </div>
          <div className="md:col-span-8 flex flex-col justify-center">
            <Reveal delay={120}>
              <p className="text-[var(--muted)]  leading-relaxed font-light max-w-[70ch]">
                No two projects need exactly the same people. Around the core team is a trusted network built over years in this industry: lighting designers, sound engineers, technical partners, immersive technology specialists, storytellers, set designers and specialist consultants. They come in when a project needs their expertise, and they join the same conversation. You should never feel like you have suddenly started managing another set of vendors.
              </p>
            </Reveal>
            <Reveal delay={200}>
              <div className="flex flex-wrap gap-x-3 gap-y-3 mt-8">
                {['Lighting Design', 'Sound Engineering', 'Rental Houses', 'Immersive Tech', 'Storytelling', 'Set Design', 'Rare Consultants'].map((c) => (
                  <span key={c} className="label  !text-[var(--muted)] border border-[var(--border)] rounded-full px-4 py-2 hover:border-[var(--accent)] hover:text-[var(--text)] transition-colors">{c}</span>
                ))}
              </div>
            </Reveal>
          </div>
        </div>

        {/* Who we work with */}
        <div className="grid md:grid-cols-12 gap-10 mb-14">
          <div className="md:col-span-7">
            <Reveal>
              <p className="label !text-[var(--accent)] label-gap">{r.forLabel}</p>
              <h2 className="font-serif-c italic text-[clamp(1.9rem,3.6vw,3rem)] leading-[1.08] text-[var(--text)]">{r.forHeading}</h2>
            </Reveal>
          </div>
          <div className="md:col-span-5 flex items-end">
            <Reveal delay={120}><p className="text-[var(--muted)] leading-relaxed font-light text-left md:text-right w-full">{r.forBody}</p></Reveal>
          </div>
        </div>
        <ClientBento clients={r.clients} />
        <Reveal delay={120}><p className="font-serif-c italic text-[var(--accent)] text-[1.6rem] md:text-[2.2rem] text-left md:text-center mt-14 md:mt-[78px]">{r.landing}</p></Reveal>
      </div>
    </section>
  );
};

export default Synthesis;
